/**
 * PinResetRequestsBanner — manager-side surface for incoming "Forgot PIN?"
 * requests from wow crew at the same store.
 *
 * Polls `/api/auth/pin-reset/pending` every 30s while a manager is logged in.
 * When one or more requests exist, slides in a sticky banner at the top of
 * the screen (above the main content) with Approve / Deny actions per row.
 *
 * Security: approval clears the target's pin_hash so they're routed to the
 * PIN-setup screen on their next sign-in attempt. The crew's polling
 * LoginPage detects the status flip and transitions automatically.
 *
 * Built 2026-06-12 per user ask: forgot-PIN flow with manager approval.
 */

import { useEffect, useState } from "react";
import axios from "axios";
import { Key, Check, X } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function initialsOf(name) {
  return (name || "?").trim().charAt(0).toUpperCase();
}

export default function PinResetRequestsBanner({ user }) {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [denyId, setDenyId] = useState(null);
  const [denyReason, setDenyReason] = useState("");

  const reload = async () => {
    if (!user?.crew_id) return;
    if (!(user.is_manager || user.is_admin || user.is_district_manager)) return;
    // Skip while tab is hidden — battery/data saver. Manager will see
    // pending PIN-reset requests the moment they come back to the app.
    // (2026-08-01)
    if (typeof document !== "undefined" && document.hidden) return;
    try {
      const r = await axios.get(`${API}/auth/pin-reset/pending`, {
        params: { manager_id: user.crew_id },
      });
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch { /* swallow */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [user?.crew_id]);
  useEffect(() => {
    if (!user?.crew_id) return undefined;
    if (!(user.is_manager || user.is_admin || user.is_district_manager)) return undefined;
    const t = setInterval(reload, 30 * 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.crew_id]);

  const approve = async (item) => {
    setBusyId(item.id);
    try {
      await axios.post(`${API}/auth/pin-reset/${item.id}/approve`, {
        actor_id: user.crew_id,
        reason: "",
      });
      setItems((cur) => cur.filter((x) => x.id !== item.id));
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't approve");
    } finally {
      setBusyId(null);
    }
  };

  const deny = async (item) => {
    setBusyId(item.id);
    try {
      await axios.post(`${API}/auth/pin-reset/${item.id}/deny`, {
        actor_id: user.crew_id,
        reason: denyReason.trim(),
      });
      setItems((cur) => cur.filter((x) => x.id !== item.id));
      setDenyId(null);
      setDenyReason("");
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't deny");
    } finally {
      setBusyId(null);
    }
  };

  if (!user?.crew_id) return null;
  if (!(user.is_manager || user.is_admin || user.is_district_manager)) return null;
  if (items.length === 0) return null;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[105] bg-gradient-to-b from-amber-50 to-amber-100/60 border-b border-amber-300 shadow-md backdrop-blur-md animate-fade-in-fast"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      data-testid="pin-reset-banner"
    >
      <div className="max-w-2xl mx-auto px-4 py-2.5 space-y-2">
        <div className="flex items-center gap-2">
          <Key weight="fill" className="w-4 h-4 text-amber-700" />
          <p className="text-[11px] uppercase tracking-wider font-black text-amber-900">
            PIN reset request{items.length > 1 ? `s · ${items.length}` : ""}
          </p>
        </div>
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-center gap-3 bg-white rounded-2xl px-3 py-2.5 shadow-sm"
            data-testid={`pin-reset-item-${item.id}`}
          >
            <div className="w-10 h-10 rounded-full bg-amber-100 overflow-hidden flex items-center justify-center text-sm font-black text-amber-900 flex-shrink-0">
              {item.crew_photo
                ? <img src={item.crew_photo} alt={item.crew_name} className="w-full h-full object-cover" />
                : initialsOf(item.crew_name)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-black text-slate-900 truncate">{item.crew_name}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">
                {item.crew_position || "Wow Crew"} · forgot PIN
              </p>
            </div>
            {denyId === item.id ? (
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  autoFocus
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value.slice(0, 80))}
                  placeholder="Why deny? (optional)"
                  className="text-[11px] px-2 h-8 rounded-lg ring-1 ring-rose-200 focus:ring-rose-400 outline-none w-32"
                  onKeyDown={(e) => { if (e.key === "Enter") deny(item); }}
                  data-testid={`pin-reset-deny-reason-${item.id}`}
                />
                <button
                  type="button"
                  onClick={() => deny(item)}
                  disabled={busyId === item.id}
                  className="h-8 px-2.5 rounded-lg bg-rose-600 text-white text-[11px] font-black uppercase tracking-wider active:scale-95"
                  data-testid={`pin-reset-confirm-deny-${item.id}`}
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={() => { setDenyId(null); setDenyReason(""); }}
                  className="h-8 w-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"
                  aria-label="Cancel deny"
                >
                  <X weight="bold" className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => approve(item)}
                  disabled={busyId === item.id}
                  className="h-9 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-wider active:scale-95 flex items-center gap-1 disabled:opacity-50"
                  data-testid={`pin-reset-approve-${item.id}`}
                >
                  <Check weight="bold" className="w-3.5 h-3.5" />
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => setDenyId(item.id)}
                  disabled={busyId === item.id}
                  className="h-9 px-3 rounded-full bg-slate-200 hover:bg-rose-200 text-slate-700 hover:text-rose-700 text-xs font-black uppercase tracking-wider active:scale-95 flex items-center gap-1 transition-colors disabled:opacity-50"
                  data-testid={`pin-reset-deny-${item.id}`}
                >
                  <X weight="bold" className="w-3.5 h-3.5" />
                  Deny
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
